import React, { useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  DescriptionRounded,
  CheckCircleRounded,
  ErrorRounded,
  OpenInNewRounded,
  ArrowForwardRounded,
} from "@mui/icons-material";
import { dt } from "../../themes/redesignTokens";
import type { ChatActionRequest } from "../models/chatBotModels";
import type { ActionResult } from "../actions/actionRegistry";
import useTranslation from "../../hooks/useTranslation";

// ============================================================
// MIDAS Action Card — Compact summary card in chat bubble.
// Full form is rendered in the artifact pane (MidasArtifactActionForm).
// This card shows: header + "review in panel" link + state feedback.
// ============================================================

const midasAction = {
  gold: "#D4A017",
  goldDark: "#A67C00",
  goldSoft: "rgba(212, 160, 23, 0.08)",
  goldBorder: "rgba(212, 160, 23, 0.20)",
  successGreen: "#27AE60",
  successBg: "rgba(39, 174, 96, 0.04)",
  errorRed: "#E74C3C",
  errorBg: "rgba(231, 76, 60, 0.04)",
};

type CardState = "pending" | "executing" | "success" | "error" | "stale" | "denied";

interface MidasActionCardProps {
  actionRequest: ChatActionRequest;
  state: CardState;
  result?: ActionResult;
  errorMessage?: string;
  onConfirm: (values: Record<string, any>) => void;
  onCancel: () => void;
  onRetry?: () => void;
  onDismiss?: () => void;
  permissionDenied?: boolean;
  /** Open the action form in the artifact pane */
  onOpenInPanel?: () => void;
}

const MidasActionCard: React.FC<MidasActionCardProps> = ({
  actionRequest,
  state,
  result,
  errorMessage,
  onConfirm,
  onCancel,
  onRetry,
  onDismiss,
  permissionDenied = false,
  onOpenInPanel,
}) => {
  const { translate } = useTranslation("chatBot");
  const isPhone = useMediaQuery("(max-width:600px)");

  // Determine active state (override with denied/stale)
  const activeState: CardState = permissionDenied ? "denied" : state;

  // Left bar color based on state
  const leftBarColor =
    activeState === "success"
      ? midasAction.successGreen
      : activeState === "error"
      ? midasAction.errorRed
      : activeState === "denied" || activeState === "stale"
      ? dt.textMuted
      : midasAction.gold;

  // Container background
  const containerBg =
    activeState === "success"
      ? midasAction.successBg
      : activeState === "error"
      ? midasAction.errorBg
      : dt.cardBg;

  // Icon for header
  const HeaderIcon =
    activeState === "success"
      ? CheckCircleRounded
      : activeState === "error"
      ? ErrorRounded
      : DescriptionRounded;

  const headerIconColor =
    activeState === "success"
      ? midasAction.successGreen
      : activeState === "error"
      ? midasAction.errorRed
      : midasAction.gold;

  const handleOpenPanel = useCallback(() => {
    if (onOpenInPanel) onOpenInPanel();
  }, [onOpenInPanel]);

  return (
    <Box
      role="region"
      aria-live="polite"
      aria-label={translate("midas.actionCard.ariaLabel", { title: actionRequest.display.title })}
      sx={{
        ml: "36px",
        mt: "8px",
        maxWidth: isPhone ? "85%" : "min(380px, 85%)",
        backgroundColor: containerBg,
        border: `1px solid ${midasAction.goldBorder}`,
        borderLeft: `3px solid ${leftBarColor}`,
        borderRadius: "4px 12px 12px 12px",
        overflow: "hidden",
        opacity: activeState === "denied" || activeState === "stale" ? 0.65 : 1,
        transition: "all 0.3s ease",
        // Pulsing animation for executing state
        ...(activeState === "executing" && {
          animation: "midasActionPulse 1.5s ease-in-out infinite",
          "@keyframes midasActionPulse": {
            "0%, 100%": { borderLeftColor: midasAction.gold },
            "50%": { borderLeftColor: midasAction.goldDark },
          },
        }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          px: "14px",
          py: "10px",
        }}
      >
        <Box
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            backgroundColor: midasAction.goldSoft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <HeaderIcon sx={{ fontSize: "18px", color: headerIconColor }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "14px",
              fontWeight: 700,
              color: dt.textPrimary,
              lineHeight: 1.3,
            }}
          >
            {/* SEC-REQ-3: plain text only */}
            {actionRequest.display.title}
          </Typography>
          {actionRequest.display.summary && (
            <Typography
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 400,
                color: dt.textSecondary,
                lineHeight: 1.3,
                mt: "2px",
              }}
            >
              {/* SEC-REQ-3: plain text only */}
              {actionRequest.display.summary}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Permission denied message */}
      {activeState === "denied" && (
        <Box sx={{ px: "14px", py: "8px" }}>
          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textMuted,
              fontStyle: "italic",
            }}
          >
            {translate("midas.actionCard.permissionDenied")}
          </Typography>
        </Box>
      )}

      {/* Stale message */}
      {activeState === "stale" && (
        <Box sx={{ px: "14px", py: "8px" }}>
          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textMuted,
              fontStyle: "italic",
            }}
          >
            {translate("midas.actionCard.expired")}
          </Typography>
        </Box>
      )}

      {/* Pending state — "Review in panel" link */}
      {activeState === "pending" && onOpenInPanel && (
        <Box sx={{ px: "14px", pb: "10px" }}>
          <Button
            onClick={handleOpenPanel}
            size="small"
            endIcon={<ArrowForwardRounded sx={{ fontSize: "14px" }} />}
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "none",
              color: midasAction.gold,
              px: "4px",
              "&:hover": {
                backgroundColor: midasAction.goldSoft,
              },
            }}
          >
            {translate("midas.actionCard.reviewInPanel")}
          </Button>
        </Box>
      )}

      {/* Executing state */}
      {activeState === "executing" && (
        <Box
          sx={{
            px: "14px",
            py: "10px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <CircularProgress size={18} sx={{ color: midasAction.gold }} />
          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textSecondary,
            }}
          >
            {translate("midas.actionCard.executing")}
          </Typography>
        </Box>
      )}

      {/* Success state */}
      {activeState === "success" && result && (
        <Box sx={{ px: "14px", py: "10px" }}>
          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: midasAction.successGreen,
              fontWeight: 600,
              mb: "6px",
            }}
          >
            {/* SEC-REQ-3: plain text only */}
            {result.message}
          </Typography>
          {result.link && (
            <Button
              href={result.link}
              size="small"
              endIcon={<OpenInNewRounded sx={{ fontSize: "14px" }} />}
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "none",
                color: midasAction.gold,
                px: "4px",
              }}
            >
              {translate("midas.actionCard.viewResult")}
            </Button>
          )}
        </Box>
      )}

      {/* Error state */}
      {activeState === "error" && (
        <Box sx={{ px: "14px", py: "10px" }}>
          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: midasAction.errorRed,
              mb: "8px",
            }}
          >
            {/* SEC-REQ-3: plain text only */}
            {errorMessage || translate("midas.actionCard.errorGeneric")}
          </Typography>
          <Box sx={{ display: "flex", gap: "8px" }}>
            {onRetry && (
              <Button
                onClick={onRetry}
                size="small"
                variant="outlined"
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderColor: midasAction.errorRed,
                  color: midasAction.errorRed,
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: midasAction.errorBg,
                    borderColor: midasAction.errorRed,
                  },
                }}
              >
                {translate("retry")}
              </Button>
            )}
            {onDismiss && (
              <Button
                onClick={onDismiss}
                size="small"
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                  color: dt.textSecondary,
                }}
              >
                {translate("midas.actionCard.dismiss")}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MidasActionCard;
